import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import {
    LocalNotifications,
    LocalNotificationSchema,
    PermissionStatus,
} from '@capacitor/local-notifications';

export interface ModuleNotification {
    id: number;
    type: 'new' | 'updated';
    moduleTitle: string;
    message: string;
    createdAt: Date;
    read: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class NotificationService {

    /**
     * Placeholder data until the backend exposes a "new/updated module" feed.
     * Kept here so the dummy list and the demo push below share one source.
     */
    private dummyModuleNotifications: ModuleNotification[] = [
        {
            id: 1,
            type: 'new',
            moduleTitle: 'Digital Marketing Mastery',
            message: '"Digital Marketing Mastery" has just been added. Start learning now!',
            createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000),
            read: false,
        },
        {
            id: 2,
            type: 'updated',
            moduleTitle: 'Web Development Fundamentals',
            message: '"Web Development Fundamentals" was updated with new lessons.',
            createdAt: new Date(Date.now() - 26 * 60 * 60 * 1000),
            read: false,
        },
        {
            id: 3,
            type: 'new',
            moduleTitle: 'Adobe Illustrator Essential Techniques',
            message: '"Adobe Illustrator Essential Techniques" has just been added.',
            createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
            read: true,
        },
    ];

    getDummyModuleNotifications(): ModuleNotification[] {
        return this.dummyModuleNotifications;
    }

    markAllModuleNotificationsRead(): void {
        this.dummyModuleNotifications.forEach((n) => (n.read = true));
    }

    /** Fires an actual system notification for a new/updated module (dummy trigger for now). */
    async notifyNewModule(notification: ModuleNotification): Promise<void> {
        await this.schedule([{
            id: notification.id,
            title: notification.type === 'new' ? 'New Module Added' : 'Module Updated',
            body: notification.message,
            schedule: { at: new Date(Date.now() + 1000) },
        }]);
    }

    async requestPermissions(): Promise<PermissionStatus | null> {
        if (!Capacitor.isNativePlatform()) {
            return null;
        }
        try {
            return await LocalNotifications.requestPermissions();
        } catch (error) {
            console.error('Error requesting local notification permissions', error);
            return null;
        }
    }

    async checkPermissions(): Promise<PermissionStatus | null> {
        if (!Capacitor.isNativePlatform()) {
            return null;
        }
        try {
            return await LocalNotifications.checkPermissions();
        } catch (error) {
            console.error('Error checking local notification permissions', error);
            return null;
        }
    }

    async schedule(notifications: LocalNotificationSchema[]): Promise<void> {
        if (!Capacitor.isNativePlatform()) {
            return;
        }
        try {
            const status = await this.checkPermissions();
            if (status?.display !== 'granted') {
                const requested = await this.requestPermissions();
                if (requested?.display !== 'granted') {
                    return;
                }
            }
            await LocalNotifications.schedule({ notifications });
        } catch (error) {
            console.error('Error scheduling local notifications', error);
        }
    }

    async cancel(ids: number[]): Promise<void> {
        if (!Capacitor.isNativePlatform()) {
            return;
        }
        try {
            await LocalNotifications.cancel({
                notifications: ids.map((id) => ({ id }))
            });
        } catch (error) {
            console.error('Error cancelling local notifications', error);
        }
    }

    async getPending() {
        if (!Capacitor.isNativePlatform()) {
            return { notifications: [] };
        }
        return await LocalNotifications.getPending();
    }

    onNotificationReceived(callback: (notification: LocalNotificationSchema) => void): void {
        if (!Capacitor.isNativePlatform()) {
            return;
        }
        LocalNotifications.addListener('localNotificationReceived', callback);
    }

    onNotificationActionPerformed(callback: (action: { notification: LocalNotificationSchema }) => void): void {
        if (!Capacitor.isNativePlatform()) {
            return;
        }
        LocalNotifications.addListener('localNotificationActionPerformed', callback);
    }
}
